// Tasting-session edge calls (ticket 302, and the sessions hub after it): create/get/list,
// host-only state transitions, participant invites, RSVP, leaving and the flight a session wraps.
// Reconciled against the real apps/edge/src/tastingSessions.ts and sessionHub.ts route tables and
// @proef/contract.
import { OkResponse } from "@proef/contract";
import { z } from "zod";
import { call } from "../edgeApi";
import {
  AttachSessionFlightRequest,
  CreateTastingSessionRequest,
  InviteToSessionRequest,
  RsvpRequest,
  ScheduleTastingSessionRequest,
  SessionList,
  TastingSessionView,
  type Rsvp,
  type SessionScope,
} from "./tastingSession";

function post(body: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}

/** What the edge takes to invite people: friends and/or one group. */
export type InviteRequest = z.input<typeof InviteToSessionRequest>;

export function createSession(
  req: z.input<typeof CreateTastingSessionRequest>,
): Promise<TastingSessionView> {
  return call(TastingSessionView, "/v1/sessions", post(CreateTastingSessionRequest.parse(req)));
}

export function getSession(sessionId: string): Promise<TastingSessionView> {
  return call(TastingSessionView, `/v1/sessions/${encodeURIComponent(sessionId)}`);
}

function transition(action: "start" | "cancel" | "end") {
  return (sessionId: string): Promise<TastingSessionView> =>
    call(TastingSessionView, `/v1/sessions/${encodeURIComponent(sessionId)}/${action}`, post({}));
}

/** `scheduledAt` is a single ISO-8601 UTC instant (DateTimeField's value),
 * required by the real `ScheduleTastingSessionRequest` -- draft -> scheduled
 * always carries a time, unlike `createSession`'s optional one. */
export const scheduleSession = (
  sessionId: string,
  scheduledAt: string,
): Promise<TastingSessionView> =>
  call(
    TastingSessionView,
    `/v1/sessions/${encodeURIComponent(sessionId)}/schedule`,
    post(ScheduleTastingSessionRequest.parse({ scheduledAt })),
  );
export const startSession = transition("start");
export const cancelSession = transition("cancel");
export const endSession = transition("end");

/** SessionInvite's target: friends and/or one group (picking a group invites
 * its current members). Route is singular `/invite`, not `/invites` -- the
 * real apps/edge/src/tastingSessions.ts route. */
export function inviteToSession(
  sessionId: string,
  req: InviteRequest,
): Promise<TastingSessionView> {
  return call(
    TastingSessionView,
    `/v1/sessions/${encodeURIComponent(sessionId)}/invite`,
    post(InviteToSessionRequest.parse(req)),
  );
}

/** One page of the hub's list for a scope; pass the previous page's `nextCursor` for the next. */
export function listSessions(scope: SessionScope, cursor?: string): Promise<SessionList> {
  const after = cursor === undefined ? "" : `&cursor=${encodeURIComponent(cursor)}`;
  return call(SessionList, `/v1/sessions?scope=${scope}${after}`);
}

/** Yes, maybe or no; the answer comes back as the session the viewer now sees. */
export function rsvpSession(sessionId: string, rsvp: Rsvp): Promise<TastingSessionView> {
  return call(
    TastingSessionView,
    `/v1/sessions/${encodeURIComponent(sessionId)}/rsvp`,
    post(RsvpRequest.parse({ rsvp })),
  );
}

export async function leaveSession(sessionId: string): Promise<void> {
  await call(OkResponse, `/v1/sessions/${encodeURIComponent(sessionId)}/leave`, post({}));
}

/** The host wraps a flight they host: the edge checks it is theirs, by device. */
export function attachSessionFlight(
  sessionId: string,
  joinCode: string,
): Promise<TastingSessionView> {
  return call(
    TastingSessionView,
    `/v1/sessions/${encodeURIComponent(sessionId)}/flight`,
    post(AttachSessionFlightRequest.parse({ joinCode })),
  );
}

/** Takes this device part in the wrapped flight, as the signed-in account. */
export function joinSessionFlight(sessionId: string): Promise<TastingSessionView> {
  return call(
    TastingSessionView,
    `/v1/sessions/${encodeURIComponent(sessionId)}/flight/join`,
    post({}),
  );
}

export type {
  ParticipantStatus,
  Rsvp,
  SessionFlightView,
  SessionList,
  SessionRoomEvent,
  SessionRoomSnapshot,
  SessionScope,
  SessionSummary,
  TastingSessionParticipantView,
  TastingSessionStatus,
  TastingSessionView,
} from "./tastingSession";
